import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  useNavigate,
  useSearch,
  useLocation,
  Outlet,
} from '@tanstack/react-router';
import {
  UploadIcon,
  TrashIcon,
  PlusIcon,
  CopyIcon,
  ArrowSquareOutIcon,
  XIcon,
  CheckCircleIcon,
  FilePlusIcon,
  CalendarBlankIcon,
  TagIcon,
  MagnifyingGlassIcon,
} from '@phosphor-icons/react';
import PaginatedGentlyTable, {
  TableColumn,
} from 'src/components/common/PaginatedGentlyTable';
import Page from 'src/components/page/Page';
import MDTypography from 'src/components/md/MDTypography';
import useRebateAgreements from 'src/api/rebates/useRebateAgreements';
import SupplierPill from 'src/components/company/SupplierPill';
import { PeriodStatusPill } from 'src/components/common/PeriodStatusPill';
import RebateAgreementUploadModal from 'src/components/rebates/RebateAgreementUploadModal';
import RebateAgreementUploadModalSimple from 'src/components/rebates/RebateAgreementUploadModalSimple';
import RebateAgreementDuplicateModal from 'src/components/rebates/RebateAgreementDuplicateModal';
import { DeleteAgreementConfirmDialog } from 'src/components/rebates/DeleteAgreementConfirmDialog';
import { BulkDeleteConfirmDialog } from 'src/components/rebates/BulkDeleteConfirmDialog';
import { BulkDuplicateConfirmDialog } from 'src/components/rebates/BulkDuplicateConfirmDialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardAction,
} from '@/components/ui/card';
import { RebateAgreementListItem } from 'src/api/rebates/types';
import { useGetDocument } from 'src/api/core/documents/useGetDocument';
import { useToast } from 'src/context/ToastContext';
import useDeleteRebateAgreement from 'src/api/rebates/useDeleteRebateAgreement';
import { useBulkDuplicateRebateAgreements } from 'src/api/rebates/useBulkDuplicateRebateAgreements';
import { useBulkDeleteRebateAgreements } from 'src/api/rebates/useBulkDeleteRebateAgreements';
import DocumentListPopover from 'src/components/rebates/DocumentListPopover';
import { RebateAgreementTagsPill } from 'src/components/rebates/RebateAgreementTagsPill';
import useRebateAgreementYears from 'src/api/rebates/useRebateAgreementYears';
import SingleSelect, {
  SingleSelectOption,
} from 'src/components/common/SingleSelect';
import { formatCurrency } from '@erp/shared-utils';
import { useBulkEnumOptionAssociations } from 'src/api/core/enumOptionAssociations/useEnumOptionAssociations';
import useCoreEnums from 'src/api/core/core-enums/useCoreEnums';
import { REBATES_SYSTEM_ENUMS } from '@erp/shared-types';
import {
  ForegroundAgentChat,
  ChartPayload,
} from '@/components/common/ForegroundAgentChat';
import {
  ChartReportModal,
  ChartData,
} from '@/components/rebates/ChartReportModal';

const useSimpleRebateAgreementUploadModal = true; // TODO make this a ff

// Helper to check if an agreement spans multiple years
const isMultiYearAgreement = (agreement: RebateAgreementListItem) => {
  const startDate = new Date(`${agreement.start_date}T00:00:00Z`);
  const endDate = new Date(`${agreement.end_date}T00:00:00Z`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return false;
  }
  return startDate.getUTCFullYear() !== endDate.getUTCFullYear();
};

// Helper to check if an agreement name indicates it's a duplicate
const isDuplicateAgreement = (agreement: RebateAgreementListItem) => {
  const name = agreement.name?.toLowerCase() ?? '';
  return name.includes('(copy)') || /\(\d{4} copy\)/.test(name);
};

const StatusPill = React.memo(function StatusPill({
  status,
}: {
  status: 'draft' | 'finalized';
}) {
  const isFinalized = status === 'finalized';
  return (
    <Badge
      variant={isFinalized ? 'default' : 'secondary'}
      className={
        isFinalized
          ? 'bg-green-100 text-green-800 hover:bg-green-100'
          : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100'
      }
    >
      {isFinalized ? 'Finalized' : 'Draft'}
    </Badge>
  );
});

const RebateAgreementsPage: React.FC = () => {
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [isManualMode, setIsManualMode] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [agreementToDelete, setAgreementToDelete] =
    useState<RebateAgreementListItem | null>(null);
  const [agreementToDuplicate, setAgreementToDuplicate] =
    useState<RebateAgreementListItem | null>(null);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [bulkDuplicateConfirmOpen, setBulkDuplicateConfirmOpen] =
    useState(false);

  // Tags filter popover state
  const [tagPopoverOpen, setTagPopoverOpen] = useState(false);
  const [tagSearchTerm, setTagSearchTerm] = useState('');

  // Chart report modal state
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [currentChart, setCurrentChart] = useState<ChartData | null>(null);
  const [offerEmailReport, setOfferEmailReport] = useState(false);

  const navigate = useNavigate();
  const { showToast } = useToast();
  const location = useLocation();

  // Bulk operation hooks
  const { bulkDuplicate, isLoading: isBulkDuplicating } =
    useBulkDuplicateRebateAgreements();
  const { bulkDelete, isLoading: isBulkDeleting } =
    useBulkDeleteRebateAgreements();

  // Read URL search params
  const searchParams = useSearch({ strict: false }) as {
    search?: string;
    year?: number;
    showClosed?: boolean;
    filterDuplicates?: boolean;
    filterFinalized?: boolean;
    filterDraft?: boolean;
    filterMultiyear?: boolean;
    filterTags?: string[];
  };
  const urlSearchQuery = searchParams.search ?? '';
  const showClosed = searchParams.showClosed ?? true; // Default: show all
  const urlYear = searchParams.year;

  // Quick filter states from URL
  const filterDuplicates = searchParams.filterDuplicates ?? false;
  const filterFinalized = searchParams.filterFinalized ?? false;
  const filterDraft = searchParams.filterDraft ?? false;
  const filterMultiyear = searchParams.filterMultiyear ?? false;
  const filterTags = searchParams.filterTags ?? [];

  // Keep typing responsive: local input state, debounced sync to URL
  const [searchInput, setSearchInput] = useState(urlSearchQuery);

  // Keep local input in sync for back/forward navigation and shared links
  useEffect(() => {
    setSearchInput(urlSearchQuery);
  }, [urlSearchQuery]);

  // Debounced URL update (replace history to avoid a new entry per keystroke)
  useEffect(() => {
    if (searchInput === urlSearchQuery) return;

    const t = window.setTimeout(() => {
      void navigate({
        to: '/rebates/agreements',
        search: (prev) => ({
          ...prev,
          search: searchInput ? searchInput : undefined,
        }),
        replace: true,
      });
    }, 200);

    return () => window.clearTimeout(t);
  }, [searchInput, urlSearchQuery, navigate]);

  // Fetch available years for the year selector
  const { data: availableYearsData } = useRebateAgreementYears();
  const availableYears =
    availableYearsData && 'years' in availableYearsData
      ? availableYearsData.years
      : undefined;

  // Default to current year if available, otherwise use URL year
  const currentYear = new Date().getFullYear();

  // Derive selectedYear: use URL if provided, otherwise default to current year when available
  const selectedYear = useMemo(() => {
    if (urlYear !== undefined) {
      return urlYear;
    }
    // Default to current year if available years are loaded and include current year
    if (availableYears && availableYears.includes(currentYear)) {
      return currentYear;
    }
    return undefined;
  }, [urlYear, availableYears, currentYear]);

  // Sync default year to URL when available years load (only if URL has no year param)
  // Only run this on the list page, not when rendering Outlet for child routes
  useEffect(() => {
    // Skip if we're on a child route (e.g., agreement detail page)
    if (location.pathname !== '/rebates/agreements') {
      return;
    }
    if (
      urlYear === undefined &&
      availableYears &&
      availableYears.includes(currentYear)
    ) {
      void navigate({
        to: '/rebates/agreements',
        search: (prev) => ({ ...prev, year: currentYear }),
        replace: true,
      });
    }
  }, [availableYears, currentYear, urlYear, navigate, location.pathname]);

  // Clear selection only when year changes (which fetches new data from API)
  // Quick filters just filter client-side data, so selection should persist
  useEffect(() => {
    setSelectedIds(new Set());
  }, [selectedYear]);

  // Update URL search params
  const setShowClosed = useCallback(
    (value: boolean) => {
      void navigate({
        to: '/rebates/agreements',
        search: (prev) => ({
          ...prev,
          showClosed: value === true ? undefined : value, // Only set in URL if false (non-default)
        }),
      });
    },
    [navigate],
  );

  // Quick filter toggle callbacks
  const toggleFilterDuplicates = useCallback(() => {
    void navigate({
      to: '/rebates/agreements',
      search: (prev) => ({
        ...prev,
        filterDuplicates: !filterDuplicates || undefined,
      }),
    });
  }, [navigate, filterDuplicates]);

  const toggleFilterFinalized = useCallback(() => {
    void navigate({
      to: '/rebates/agreements',
      search: (prev) => ({
        ...prev,
        filterFinalized: !filterFinalized || undefined,
        // Disable draft filter when enabling finalized (mutually exclusive)
        filterDraft: !filterFinalized ? undefined : filterDraft || undefined,
      }),
    });
  }, [navigate, filterFinalized, filterDraft]);

  const toggleFilterDraft = useCallback(() => {
    void navigate({
      to: '/rebates/agreements',
      search: (prev) => ({
        ...prev,
        filterDraft: !filterDraft || undefined,
        // Disable finalized filter when enabling draft (mutually exclusive)
        filterFinalized: !filterDraft
          ? undefined
          : filterFinalized || undefined,
      }),
    });
  }, [navigate, filterDraft, filterFinalized]);

  const toggleFilterMultiyear = useCallback(() => {
    void navigate({
      to: '/rebates/agreements',
      search: (prev) => ({
        ...prev,
        filterMultiyear: !filterMultiyear || undefined,
      }),
    });
  }, [navigate, filterMultiyear]);

  const setFilterTags = useCallback(
    (tagIds: string[]) => {
      void navigate({
        to: '/rebates/agreements',
        search: (prev) => ({
          ...prev,
          filterTags: tagIds.length > 0 ? tagIds : undefined,
        }),
      });
    },
    [navigate],
  );

  const setSelectedYear = useCallback(
    (year: number | undefined) => {
      void navigate({
        to: '/rebates/agreements',
        search: (prev) => ({ ...prev, year }),
      });
    },
    [navigate],
  );

  // Build query params with year filter (convert single year to array for API)
  const queryParams = useMemo(
    () => ({
      years: selectedYear !== undefined ? [selectedYear] : undefined,
    }),
    [selectedYear],
  );

  // Fetch rebate agreements data (includes metrics)
  const { data, isLoading, error, refetch } = useRebateAgreements(queryParams);

  // Filter data based on showClosed state, search query, and quick filters
  const filteredData = useMemo(() => {
    if (!data) return [];
    let result = data;

    // Filter by closed status
    if (!showClosed) {
      result = result.filter((agreement) => !agreement.isClosedOut);
    }

    // Filter by search query (name field)
    if (searchInput) {
      const lowerSearch = searchInput.toLowerCase();
      result = result.filter((agreement) =>
        agreement.name?.toLowerCase().includes(lowerSearch),
      );
    }

    // Quick filters
    if (filterDuplicates) {
      result = result.filter((agreement) => isDuplicateAgreement(agreement));
    }

    if (filterFinalized) {
      result = result.filter((agreement) => agreement.status === 'finalized');
    }

    if (filterDraft) {
      result = result.filter((agreement) => agreement.status === 'draft');
    }

    if (filterMultiyear) {
      result = result.filter((agreement) => isMultiYearAgreement(agreement));
    }

    return result;
  }, [
    data,
    showClosed,
    searchInput,
    filterDuplicates,
    filterFinalized,
    filterDraft,
    filterMultiyear,
  ]);

  // Prepare entities for bulk tag fetching
  const agreementTagEntities = useMemo(() => {
    if (!data || data.length === 0) return undefined;
    return data.map((agreement) => ({
      entityType: 'rebate_agreement',
      entityId: agreement.id,
    }));
  }, [data]);

  // Fetch tags for all agreements in one request
  const { data: agreementTagAssociations } = useBulkEnumOptionAssociations(
    agreementTagEntities,
    !!agreementTagEntities && agreementTagEntities.length > 0,
  );

  // Fetch enum definition for tag display names (once for all agreements)
  const { data: agreementTagsEnum } = useCoreEnums(
    {
      name: REBATES_SYSTEM_ENUMS.AGREEMENT_TAGS.name,
      includeOptions: true,
    },
    {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
    },
  );

  const availableAgreementTags = useMemo(
    () => agreementTagsEnum?.edges?.[0]?.options ?? [],
    [agreementTagsEnum],
  );

  // Create map of agreementId -> tags
  const agreementTagsMap = useMemo(() => {
    if (!agreementTagAssociations) return new Map();

    const map = new Map<string, typeof agreementTagAssociations>();
    agreementTagAssociations.forEach((association) => {
      const existing = map.get(association.entityId) || [];
      map.set(association.entityId, [...existing, association]);
    });
    return map;
  }, [agreementTagAssociations]);

  // Serialize filterTags for stable dependency comparison
  const filterTagsKey = filterTags.join(',');

  // Apply tag filtering on top of other filters (needs agreementTagsMap)
  const finalFilteredData = useMemo(() => {
    if (filterTags.length === 0) return filteredData;

    return filteredData.filter((agreement) => {
      const tags = agreementTagsMap.get(agreement.id) || [];
      // Check if agreement has at least one of the selected tags
      // Note: tag associations use coreEnumOptionId to reference the enum option
      return tags.some((tag: { coreEnumOptionId: string }) =>
        filterTags.includes(tag.coreEnumOptionId),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData, filterTagsKey, agreementTagsMap]);

  // Convert available tags to filter options
  const tagFilterOptions = useMemo(() => {
    return availableAgreementTags
      .filter((tag) => tag.name !== null)
      .map((tag) => ({
        id: tag.id,
        label: tag.name ?? '',
      }));
  }, [availableAgreementTags]);

  // Filter tag options by search term
  const filteredTagOptions = useMemo(() => {
    if (!tagSearchTerm) return tagFilterOptions;
    const lowerSearch = tagSearchTerm.toLowerCase();
    return tagFilterOptions.filter((opt) =>
      opt.label.toLowerCase().includes(lowerSearch),
    );
  }, [tagFilterOptions, tagSearchTerm]);

  // Toggle a single tag in the filter
  const toggleTagFilter = useCallback(
    (tagId: string) => {
      const newTags = filterTags.includes(tagId)
        ? filterTags.filter((id) => id !== tagId)
        : [...filterTags, tagId];
      setFilterTags(newTags);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterTagsKey, setFilterTags],
  );

  // Select/deselect all visible tags
  const toggleAllTags = useCallback(() => {
    const visibleTagIds = filteredTagOptions.map((opt) => opt.id);
    const allSelected = visibleTagIds.every((id) => filterTags.includes(id));
    if (allSelected) {
      // Deselect all visible
      setFilterTags(filterTags.filter((id) => !visibleTagIds.includes(id)));
    } else {
      // Select all visible
      const newTags = [...new Set([...filterTags, ...visibleTagIds])];
      setFilterTags(newTags);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTagOptions, filterTagsKey, setFilterTags]);

  // Clear tag search when popover closes
  const handleTagPopoverOpenChange = useCallback((open: boolean) => {
    setTagPopoverOpen(open);
    if (!open) {
      setTagSearchTerm('');
    }
  }, []);

  // Document fetching - on-demand POST request
  const { mutate: fetchDocument } = useGetDocument();

  // Delete agreement mutation
  const deleteAgreementMutation = useDeleteRebateAgreement(
    agreementToDelete?.id || '',
  );

  const handleViewDocument = useCallback(
    (documentId: string) => {
      fetchDocument(
        { documentId },
        {
          onSuccess: (response) => {
            if (response.data.signedUrl) {
              window.open(response.data.signedUrl, '_blank');
            }
          },
          onError: (err) => {
            console.error('Error fetching document:', err);
            showToast({
              message: 'Failed to load document.',
              title: 'Document Error',
              color: 'error',
              icon: 'warning',
            });
          },
        },
      );
    },
    [fetchDocument, showToast],
  );

  const handleRowClick = useCallback(
    (agreement: RebateAgreementListItem) => {
      navigate({
        to: '/rebates/agreements/$id',
        params: { id: agreement.id },
      });
    },
    [navigate],
  );

  const handleOpenInNewTab = useCallback(
    (agreement: RebateAgreementListItem) => {
      window.open(`/rebates/agreements/${agreement.id}`, '_blank');
    },
    [],
  );

  const handleDuplicateAgreement = useCallback(
    (agreement: RebateAgreementListItem) => {
      setAgreementToDuplicate(agreement);
      setDuplicateModalOpen(true);
    },
    [],
  );

  const handleDeleteAgreement = useCallback(
    (agreement: RebateAgreementListItem) => {
      setAgreementToDelete(agreement);
      setDeleteConfirmOpen(true);
    },
    [],
  );

  // Selection handlers
  const toggleSelectAll = useCallback(() => {
    if (!finalFilteredData) return;
    const allIds = finalFilteredData.map((a) => a.id);
    setSelectedIds((prev) => {
      const allSelected = allIds.every((id) => prev.has(id));
      if (allSelected) {
        return new Set();
      } else {
        return new Set(allIds);
      }
    });
  }, [finalFilteredData]);

  const toggleSelectOne = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Get selected agreements for bulk operations
  const selectedAgreements = useMemo(() => {
    if (!finalFilteredData) return [];
    return finalFilteredData.filter((a) => selectedIds.has(a.id));
  }, [finalFilteredData, selectedIds]);

  // Check if all visible items are selected
  const allSelected = useMemo(() => {
    if (!finalFilteredData || finalFilteredData.length === 0) return false;
    return finalFilteredData.every((a) => selectedIds.has(a.id));
  }, [finalFilteredData, selectedIds]);

  const someSelected = selectedIds.size > 0;

  // Memoize checkbox column to prevent recreation on every render
  const checkboxColumn = useMemo<TableColumn<RebateAgreementListItem>>(
    () => ({
      id: 'select' as keyof RebateAgreementListItem,
      label: '',
      sortable: false,
      minWidth: '40px',
      stopRowClick: true,
      headerRender: () => (
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={allSelected && finalFilteredData.length > 0}
            onCheckedChange={toggleSelectAll}
            aria-label="Select all agreements"
          />
        </div>
      ),
      render: (_, row) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={selectedIds.has(row.id)}
            onCheckedChange={() => toggleSelectOne(row.id)}
            aria-label={`Select ${row.name}`}
          />
        </div>
      ),
    }),
    [
      allSelected,
      finalFilteredData.length,
      toggleSelectAll,
      selectedIds,
      toggleSelectOne,
    ],
  );

  const columns: TableColumn<RebateAgreementListItem>[] = useMemo(
    () => [
      // Checkbox column for bulk selection
      checkboxColumn,
      {
        id: 'index' as keyof RebateAgreementListItem,
        label: '#',
        sortable: false,
        minWidth: '50px',
      },
      {
        id: 'name',
        label: 'Agreement Name',
        sortable: true,
      },
      {
        id: 'supplier.name' as keyof RebateAgreementListItem,
        label: 'Supplier',
        sortable: true,
        render: (_value, row) => <SupplierPill supplier={row.supplier} />,
      },
      {
        id: 'metrics.ytdRebate' as keyof RebateAgreementListItem,
        label: 'YTD Rebate',
        sortable: true,
        render: (_value, row) => {
          if (!row.metrics || row.metrics.ytdRebate === 0) {
            return <span className="text-sm text-muted-foreground">-</span>;
          }
          return (
            <span className="text-sm font-medium text-green-700">
              {formatCurrency(row.metrics.ytdRebate)}
            </span>
          );
        },
      },
      {
        id: 'metrics.ytdTotalVolume' as keyof RebateAgreementListItem,
        label: 'YTD Volume',
        sortable: true,
        render: (_value, row) => {
          if (!row.metrics || row.metrics.ytdTotalVolume === 0) {
            return <span className="text-sm text-muted-foreground">-</span>;
          }
          return (
            <span className="text-sm text-muted-foreground">
              {formatCurrency(row.metrics.ytdTotalVolume)}
            </span>
          );
        },
      },
      {
        id: 'metrics.ytdRebatePercentage' as keyof RebateAgreementListItem,
        label: 'Rebate %',
        sortable: true,
        render: (_value, row) => {
          if (!row.metrics || row.metrics.ytdTotalVolume === 0) {
            return <span className="text-sm text-muted-foreground">-</span>;
          }
          return (
            <Badge
              variant="outline"
              className="bg-purple-50 text-purple-700 border-purple-200"
            >
              {row.metrics.ytdRebatePercentage.toFixed(2)}%
            </Badge>
          );
        },
      },
      {
        id: 'status' as keyof RebateAgreementListItem,
        label: 'Finalized?',
        sortable: true,
        render: (value: 'draft' | 'finalized') => <StatusPill status={value} />,
      },
      {
        id: 'closure_status' as keyof RebateAgreementListItem,
        label: 'Closure Status',
        sortable: true,
        render: (_value, row) =>
          row.isClosedOut ? (
            <Badge variant="outline" className="bg-slate-100">
              Closed
            </Badge>
          ) : (
            <Badge variant="default">Open</Badge>
          ),
      },
      {
        id: 'version_number' as keyof RebateAgreementListItem,
        label: 'Version',
        sortable: true,
        render: (value: number) => (
          <Badge variant="outline" className="font-mono">
            v{value}
          </Badge>
        ),
      },
      {
        id: 'period' as keyof RebateAgreementListItem,
        label: 'Status',
        sortable: true,
        render: (_value, row) => (
          <PeriodStatusPill startDate={row.start_date} endDate={row.end_date} />
        ),
      },
      {
        id: 'payment_terms' as keyof RebateAgreementListItem,
        label: 'Payment Terms',
        sortable: true,
        render: (value: string | null) => (
          <span className="text-sm">
            {value || (
              <span className="text-muted-foreground italic">
                Not specified
              </span>
            )}
          </span>
        ),
      },
      {
        id: 'start_date',
        label: 'Start Date',
        sortable: true,
        render: (value: string) => (
          <MDTypography variant="body2">
            {new Intl.DateTimeFormat('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              timeZone: 'UTC',
            }).format(new Date(value))}
          </MDTypography>
        ),
      },
      {
        id: 'end_date',
        label: 'End Date',
        sortable: true,
        render: (value: string) => (
          <MDTypography variant="body2">
            {new Intl.DateTimeFormat('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              timeZone: 'UTC',
            }).format(new Date(value))}
          </MDTypography>
        ),
      },
      {
        id: 'documents',
        label: 'Documents',
        minWidth: '120px',
        render: (_value, row) => (
          <div onClick={(e) => e.stopPropagation()}>
            <DocumentListPopover
              documents={row.documents || []}
              onDocumentClick={handleViewDocument}
              mode="compact"
            />
          </div>
        ),
      },
      {
        id: 'tags' as keyof RebateAgreementListItem,
        label: 'Tags',
        minWidth: '120px',
        render: (_value, row) => (
          <RebateAgreementTagsPill
            agreementId={row.id}
            tags={agreementTagsMap.get(row.id) || []}
            availableTags={availableAgreementTags}
          />
        ),
      },
      {
        id: 'actions' as keyof RebateAgreementListItem,
        label: 'Actions',
        minWidth: '100px',
        render: (_value, row) => (
          <div onClick={(e) => e.stopPropagation()} className="flex gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenInNewTab(row)}
                    className="h-8 w-8 p-0 text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                  >
                    <ArrowSquareOutIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Open in New Tab</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDuplicateAgreement(row)}
                    className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  >
                    <CopyIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Duplicate Agreement</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteAgreement(row)}
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Delete Agreement</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ),
      },
    ],
    [
      checkboxColumn,
      agreementTagsMap,
      availableAgreementTags,
      handleViewDocument,
      handleOpenInNewTab,
      handleDuplicateAgreement,
      handleDeleteAgreement,
    ],
  );

  const handleUploadClick = () => {
    setIsManualMode(false);
    setUploadModalOpen(true);
  };

  const handleManualCreateClick = () => {
    setIsManualMode(true);
    setUploadModalOpen(true);
  };

  const handleModalClose = () => {
    setUploadModalOpen(false);
    setIsManualMode(false);
  };

  const handleDuplicateModalClose = () => {
    setDuplicateModalOpen(false);
    setAgreementToDuplicate(null);
  };

  // Chart modal handlers
  const handleShowChart = useCallback((payload: ChartPayload) => {
    setCurrentChart(payload.chart);
    setOfferEmailReport(payload.offerEmailReport);
    setChartModalOpen(true);
  }, []);

  const handleChartModalClose = useCallback(() => {
    setChartModalOpen(false);
    // Keep chart data for a moment to prevent flicker during close animation
    setTimeout(() => {
      setCurrentChart(null);
      setOfferEmailReport(false);
    }, 200);
  }, []);

  const handleEmailReport = useCallback(() => {
    showToast({
      message: 'Report queued to be sent!',
      title: 'Email Report',
      color: 'success',
      icon: 'check',
    });
    handleChartModalClose();
  }, [showToast, handleChartModalClose]);

  const handleSwitchToManualMode = () => {
    setIsManualMode(true);
  };

  const handleUploadSuccess = (agreementId?: string) => {
    refetch();
    setUploadModalOpen(false);
    setIsManualMode(false);

    // Navigate to the agreement detail page if we have an ID
    if (agreementId) {
      navigate({
        to: '/rebates/agreements/$id',
        params: {
          id: agreementId,
        },
        search: {}, // Clear search params when navigating to detail page
      });
    }
  };

  const handleDuplicateSuccess = (agreementId?: string) => {
    refetch();
    setDuplicateModalOpen(false);
    setAgreementToDuplicate(null);

    // Navigate to the agreement detail page if we have an ID
    if (agreementId) {
      navigate({
        to: '/rebates/agreements/$id',
        params: {
          id: agreementId,
        },
        search: {}, // Clear search params when navigating to detail page
      });
    }
  };

  const handleDeleteDialogClose = () => {
    setDeleteConfirmOpen(false);
    setAgreementToDelete(null);
  };

  const confirmDeleteAgreement = async () => {
    if (!agreementToDelete) return;

    try {
      await deleteAgreementMutation.mutateAsync();
      refetch(); // Refresh the list
      handleDeleteDialogClose();
    } catch (error) {
      console.error('Error deleting agreement:', error);
      showToast({
        message: 'Failed to delete rebate agreement',
        color: 'error',
      });
    }
  };

  // Bulk action handlers
  const handleBulkDuplicateClick = useCallback(() => {
    if (selectedAgreements.length === 0) return;
    setBulkDuplicateConfirmOpen(true);
  }, [selectedAgreements]);

  const handleBulkDuplicateConfirm = useCallback(
    async (year: number, agreements: RebateAgreementListItem[]) => {
      if (agreements.length === 0) return;

      await bulkDuplicate(agreements, year);
      clearSelection();
      setBulkDuplicateConfirmOpen(false);
      refetch();
    },
    [bulkDuplicate, clearSelection, refetch],
  );

  const handleBulkDuplicateDialogClose = () => {
    setBulkDuplicateConfirmOpen(false);
  };

  const handleBulkDeleteClick = useCallback(() => {
    if (selectedAgreements.length === 0) return;
    setBulkDeleteConfirmOpen(true);
  }, [selectedAgreements]);

  const handleBulkDeleteConfirm = async () => {
    if (selectedAgreements.length === 0) return;

    await bulkDelete(selectedAgreements);
    clearSelection();
    setBulkDeleteConfirmOpen(false);
    refetch();
  };

  const handleBulkDeleteDialogClose = () => {
    setBulkDeleteConfirmOpen(false);
  };

  const headerActions = (
    <div className="flex gap-2">
      <Button variant="outline" onClick={handleManualCreateClick}>
        <PlusIcon className="mr-2 h-4 w-4" />
        Create Manually
      </Button>
      <Button onClick={handleUploadClick}>
        <UploadIcon className="mr-2 h-4 w-4" />
        Upload Agreement
      </Button>
    </div>
  );

  // Convert available years to SingleSelect options
  const yearOptions: SingleSelectOption[] = useMemo(() => {
    if (!availableYears || availableYears.length === 0) return [];
    return availableYears
      .sort((a, b) => b - a) // Sort descending (newest first)
      .map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
  }, [availableYears]);

  // Convert selected year to SingleSelect option
  const selectedYearOption = useMemo(() => {
    if (selectedYear === undefined) return null;
    return yearOptions.find((option) => option.value === selectedYear) || null;
  }, [yearOptions, selectedYear]);

  const handleYearChange = (option: SingleSelectOption | null) => {
    setSelectedYear(option ? (option.value as number) : undefined);
  };

  // Custom count display that includes bulk controls
  const customCountDisplay = useMemo(() => {
    if (isLoading || !finalFilteredData) return null;

    return (
      <div className="flex items-center gap-4">
        <div className="text-sm text-muted-foreground">
          Showing {finalFilteredData.length}{' '}
          {finalFilteredData.length === 1 ? 'item' : 'items'}
        </div>
        {/* Bulk actions bar - appears when items are selected */}
        {someSelected && (
          <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-md border border-blue-200">
            <span className="text-sm font-medium text-blue-800">
              {selectedIds.size} selected
            </span>
            <div className="h-4 w-px bg-blue-200" />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBulkDuplicateClick}
                    disabled={isBulkDuplicating}
                    className="h-7 px-2 text-blue-700 hover:text-blue-800 hover:bg-blue-100"
                  >
                    <CopyIcon className="h-4 w-4 mr-1" />
                    Duplicate
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Duplicate selected agreements</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBulkDeleteClick}
                    disabled={isBulkDeleting}
                    className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <TrashIcon className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Delete selected agreements</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSelection}
              className="h-7 w-7 p-0 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    );
  }, [
    isLoading,
    finalFilteredData,
    someSelected,
    selectedIds.size,
    handleBulkDuplicateClick,
    isBulkDuplicating,
    handleBulkDeleteClick,
    isBulkDeleting,
    clearSelection,
  ]);

  const tableActions = (
    <div className="flex items-center gap-4">
      {/* Quick filter buttons */}
      <div className="flex items-center gap-1 border-r pr-4">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={filterDuplicates ? 'default' : 'ghost'}
                size="sm"
                onClick={toggleFilterDuplicates}
                className={`h-8 w-8 p-0 ${
                  filterDuplicates
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'text-gray-600 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                <CopyIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {filterDuplicates ? 'Showing duplicates' : 'Filter duplicates'}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={filterFinalized ? 'default' : 'ghost'}
                size="sm"
                onClick={toggleFilterFinalized}
                className={`h-8 w-8 p-0 ${
                  filterFinalized
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'text-gray-600 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                <CheckCircleIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {filterFinalized ? 'Showing finalized' : 'Filter finalized'}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={filterDraft ? 'default' : 'ghost'}
                size="sm"
                onClick={toggleFilterDraft}
                className={`h-8 w-8 p-0 ${
                  filterDraft
                    ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                    : 'text-gray-600 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                <FilePlusIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{filterDraft ? 'Showing drafts' : 'Filter drafts'}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={filterMultiyear ? 'default' : 'ghost'}
                size="sm"
                onClick={toggleFilterMultiyear}
                className={`h-8 w-8 p-0 ${
                  filterMultiyear
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'text-gray-600 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                <CalendarBlankIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {filterMultiyear ? 'Showing multi-year' : 'Filter multi-year'}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {/* Tags filter icon with dropdown */}
        {tagFilterOptions.length > 0 && (
          <Popover
            open={tagPopoverOpen}
            onOpenChange={handleTagPopoverOpenChange}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <Button
                      variant={filterTags.length > 0 ? 'default' : 'ghost'}
                      size="sm"
                      className={`h-8 p-0 ${
                        filterTags.length > 0
                          ? 'bg-orange-600 text-white hover:bg-orange-700 w-auto px-2 gap-1'
                          : 'text-gray-600 hover:text-gray-700 hover:bg-gray-100 w-8'
                      }`}
                    >
                      <TagIcon className="h-4 w-4" />
                      {filterTags.length > 0 && (
                        <span className="text-xs font-medium">
                          {filterTags.length}
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {filterTags.length > 0
                      ? `Filtering by ${filterTags.length} tag${filterTags.length > 1 ? 's' : ''}`
                      : 'Filter by tags'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <PopoverContent className="w-64 p-0" align="start">
              {/* Search input */}
              <div className="flex items-center gap-2 p-2 border-b">
                <MagnifyingGlassIcon className="h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search tags..."
                  value={tagSearchTerm}
                  onChange={(e) => setTagSearchTerm(e.target.value)}
                  className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
                />
                {tagSearchTerm && (
                  <button
                    type="button"
                    onClick={() => setTagSearchTerm('')}
                    className="p-1 hover:bg-accent rounded-sm"
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                )}
              </div>
              {/* Select All / Deselect All */}
              {filteredTagOptions.length > 0 && (
                <div
                  className="flex items-center gap-2 px-3 py-2 border-b hover:bg-accent cursor-pointer"
                  onClick={toggleAllTags}
                >
                  <Checkbox
                    checked={
                      filteredTagOptions.every((opt) =>
                        filterTags.includes(opt.id),
                      )
                        ? true
                        : filteredTagOptions.some((opt) =>
                              filterTags.includes(opt.id),
                            )
                          ? 'indeterminate'
                          : false
                    }
                    className="pointer-events-none"
                  />
                  <span className="text-sm font-medium">
                    {filteredTagOptions.every((opt) =>
                      filterTags.includes(opt.id),
                    )
                      ? 'Deselect All'
                      : 'Select All'}
                  </span>
                </div>
              )}
              {/* Tag options list */}
              <div className="max-h-[200px] overflow-y-auto">
                {filteredTagOptions.length > 0 ? (
                  filteredTagOptions.map((opt) => (
                    <div
                      key={opt.id}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-accent cursor-pointer"
                      onClick={() => toggleTagFilter(opt.id)}
                    >
                      <Checkbox
                        checked={filterTags.includes(opt.id)}
                        className="pointer-events-none"
                      />
                      <span className="text-sm">{opt.label}</span>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    No tags found
                  </div>
                )}
              </div>
              {/* Clear button */}
              {filterTags.length > 0 && (
                <div className="border-t p-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-muted-foreground hover:text-foreground"
                    onClick={() => setFilterTags([])}
                  >
                    Clear all tags
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Switch
          id="show-closed"
          checked={showClosed}
          onCheckedChange={setShowClosed}
        />
        <Label htmlFor="show-closed" className="text-sm cursor-pointer">
          Show Closed
        </Label>
      </div>
      <SingleSelect
        labelPosition="left"
        options={yearOptions}
        value={selectedYearOption}
        onChange={handleYearChange}
        placeholder="Select year..."
        searchPlaceholder="Search years..."
        size="sm"
        shouldAutoSort={false}
        allowClear={false}
      />
    </div>
  );

  // Check if we're on a child route (e.g., agreement detail page)
  const isChildRoute = location.pathname !== '/rebates/agreements';

  // If we're on a child route, just render the Outlet for the child component
  if (isChildRoute) {
    return <Outlet />;
  }

  return (
    <>
      <Page isLoading={isLoading} error={error ?? undefined}>
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold">
              Rebate Agreements
            </CardTitle>
            <CardAction>{headerActions}</CardAction>
          </CardHeader>
          <CardContent>
            <PaginatedGentlyTable<RebateAgreementListItem>
              data={finalFilteredData || []}
              columns={columns}
              isLoading={isLoading}
              searchPlaceholder="Search agreements..."
              onRowClick={handleRowClick}
              emptyMessage="No rebate agreements found"
              searchFields={['name']}
              showSearch={true}
              showCount={true}
              showDownload={false}
              actions={tableActions}
              customCountDisplay={customCountDisplay}
              serverSideSearch={{
                value: searchInput,
                onChange: setSearchInput,
              }}
              paginationConfig={{
                enabled: false,
                pagination: {
                  currentPage: 1,
                  pageSize: 50,
                  hasNextPage: false,
                  hasPreviousPage: false,
                },
                onPageSizeChange: () => {},
                onPreviousPage: () => {},
                onNextPage: () => {},
                onFirstPage: () => {},
                showRowsPerPage: false,
                showPageInfo: false,
              }}
              defaultSort={{
                sortBy: 'name',
                sortDirection: 'asc',
              }}
            />
          </CardContent>
        </Card>
      </Page>

      {useSimpleRebateAgreementUploadModal && !isManualMode ? (
        <RebateAgreementUploadModalSimple
          open={uploadModalOpen}
          onClose={handleModalClose}
          onSuccess={handleUploadSuccess}
        />
      ) : (
        <RebateAgreementUploadModal
          open={uploadModalOpen}
          onClose={handleModalClose}
          onSuccess={handleUploadSuccess}
          isManualMode={isManualMode}
          onSwitchToManualMode={handleSwitchToManualMode}
          agreements={data || []}
          agreementToDuplicate={agreementToDuplicate}
        />
      )}

      {/* Duplicate Agreement Modal */}
      <RebateAgreementDuplicateModal
        open={duplicateModalOpen}
        onClose={handleDuplicateModalClose}
        onSuccess={handleDuplicateSuccess}
        sourceAgreement={agreementToDuplicate}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteAgreementConfirmDialog
        open={deleteConfirmOpen}
        onClose={handleDeleteDialogClose}
        onConfirm={confirmDeleteAgreement}
        agreementName={agreementToDelete?.name}
        loading={deleteAgreementMutation.isLoading}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <BulkDeleteConfirmDialog
        open={bulkDeleteConfirmOpen}
        onClose={handleBulkDeleteDialogClose}
        onConfirm={handleBulkDeleteConfirm}
        agreements={selectedAgreements}
        loading={isBulkDeleting}
      />

      {/* Bulk Duplicate Confirmation Dialog */}
      <BulkDuplicateConfirmDialog
        open={bulkDuplicateConfirmOpen}
        onClose={handleBulkDuplicateDialogClose}
        onConfirm={handleBulkDuplicateConfirm}
        agreements={selectedAgreements}
        loading={isBulkDuplicating}
      />

      {/* AI Chat Assistant */}
      <ForegroundAgentChat
        moduleKey="rebates"
        currentPage="/rebates/agreements"
        onNavigate={(path, options) =>
          navigate({
            to: path,
            search: options?.search as Record<string, unknown>,
          })
        }
        onShowChart={handleShowChart}
      />

      {/* Chart Report Modal */}
      <ChartReportModal
        open={chartModalOpen}
        onClose={handleChartModalClose}
        chart={currentChart}
        offerEmailReport={offerEmailReport}
        onEmailReport={handleEmailReport}
      />
    </>
  );
};

export default RebateAgreementsPage;
